/*
  An implementation of Web Components-like Slots:
  https://developer.mozilla.org/en-US/docs/Web/Web_Components/Using_templates_and_slots

  Slots provide a way to distribute content into structured templates.

  For example, let's say you have a component that defines a layout:

  function Layout() {
    return (
      <div>
        <header>Title</header>

        <div class="container">
          <div>Body goes here</div>
        </div>

        <footer>Footer goes here</footer>
      </div>
    );
  }

  You'd like to use this Layout component in other components and inject content
  into the various sections. Slots solves this problem like so:

  Define Slots within Layout where content will be injected:

  function Layout({ children }) {
    return (
      <SlotScope>
        <div>
          <Slot name="header" as="header" />

          <div class="container">
            {children}
          </div>

          <Slot name="footer" as="footer" />
        </div>
      </SlotScope>
    );
  }

  SlotScope scopes content to be used to fill Slots. By using the children prop, Slots will
  be filled by content defined in the children components.

  Use the Layout:

  function Movie() {
    return (
      <Layout>
        <SlotContent name="header">The Big Lebowski</SlotContent>

        Definitely, the <em>best</em> movie ever.

        <SlotContent name="footer">
          <a href="https://www.imdb.com/title/tt0118715/">Read more</a>
        </SlotContent>
      </Layout>
    );
  }

  The SlotContent component defines content to be used to fill Slots having the same name.

  The rendered content:

    <div>
      <header>The Big Lebowski</header>

      <div class="container">
        Definitely, the <em>best</em> movie ever.
      </div>

      <footer>
        <a href="https://www.imdb.com/title/tt0118715/">Read more</a>
      </footer>
    </div>
 */

import React from "react";
import PropTypes from "prop-types";

const SlotContext = React.createContext();

export class Slot extends React.Component {
  static propTypes = {
    name: PropTypes.string,
    as: PropTypes.any
  };

  static defaultProps = {
    as: React.Fragment
  };

  static contextType = SlotContext;

  render() {
    const { name, as: type, ...props } = this.props;

    const content = this.context.slotContent(name);

    const { children, ...contentProps } = content.props;

    return React.createElement(
      type,
      { ...props, ...contentProps },
      content.props.children
    );
  }
}

export function SlotContent() {
  return '';
}

SlotContent.propTypes = {
  children: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired
};

function deepWalk(nodes, callback) {
  const trees = [].concat(nodes);

  while (trees.length > 0) {
    const tree = trees.shift();

    React.Children.forEach(tree, (node) => {
      if (React.isValidElement(node)) {
        callback(node);

        if (node.type !== SlotScope && React.Children.count(node) !== 0) {
          trees.push(node.props.children);
        }
      }
    });
  }
}

export class SlotScope extends React.Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    content: PropTypes.node
  };

  contentTree() {
    return this.props.content || this.props.children;
  }

  slotContent(name) {
    let contentNode = null;

    deepWalk(this.contentTree(), (node) => {
      if (node.type === SlotContent && node.props.name === name) {
        contentNode = node;
      }
    });

    return contentNode || <React.Fragment/>;
  }

  render() {
    const { children } = this.props;

    return (
      <SlotContext.Provider value={this}>
        {children}
      </SlotContext.Provider>
    );
  }
}
